import React, { useEffect, useRef, useState } from 'react';
import '../styles/Footer.css';

const QUICK_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About Us' },
  { href: '/services', label: 'Services' },
  { href: '/tariff', label: 'Tariff & Terms' },
  { href: '/contact', label: 'Contact' },
  { href: '/taxi-routes', label: 'All Taxi Routes' },
  { href: '/blog', label: 'Blog' },
  { href: '/coimbatore-taxi', label: 'Coimbatore Taxi' },
  { href: '/chennai-taxi', label: 'Chennai Taxi' },
  { href: '/madurai-taxi', label: 'Madurai Taxi' },
  { href: '/trichy-taxi', label: 'Trichy Taxi' },
  { href: '/bangalore-taxi', label: 'Bangalore Taxi' },
  { href: '/terms-and-conditions', label: 'Terms & Conditions' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/booking-policy', label: 'Booking Policy' },
  { href: '/cancellation-refund-policy', label: 'Cancellation & Refund Policy' },
  { href: '/fare-payment-policy', label: 'Fare & Payment Policy' },
  { href: '/customer-account-deletion', label: 'Customer Account Deletion' },
];

const SERVICE_LINKS = [
  { href: '/', label: 'Airport Transfer' },
  { href: '/', label: 'City Rides' },
  { href: '/', label: 'Outstation' },
  { href: '/', label: 'Corporate' },
  { href: '/', label: 'Special Events' },
];

const DRIVER_LINKS = [
  { href: '/driver-privacy-policy', label: 'Driver Privacy Policy' },
  { href: '/driver-terms', label: 'Driver Terms & Conditions' },
  { href: '/driver-data-deletion', label: 'Account & Data Deletion' },
  { href: '/driver-support', label: 'Driver Support' },
];

const EXPLORE_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About Us' },
  { href: '/services', label: 'Services' },
  { href: '/tariff', label: 'Tariff & Terms' },
  { href: '/blog', label: 'Blog' },
  ...SERVICE_LINKS,
];

const ROUTE_LINKS = [
  { href: '/taxi-routes', label: 'All Taxi Routes' },
  { href: '/coimbatore-taxi', label: 'Coimbatore Taxi' },
  { href: '/chennai-taxi', label: 'Chennai Taxi' },
  { href: '/madurai-taxi', label: 'Madurai Taxi' },
  { href: '/trichy-taxi', label: 'Trichy Taxi' },
  { href: '/bangalore-taxi', label: 'Bangalore Taxi' },
];

const HELP_LINKS = [
  { href: '/terms-and-conditions', label: 'Terms & Conditions' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/booking-policy', label: 'Booking Policy' },
  { href: '/cancellation-refund-policy', label: 'Cancellation & Refund Policy' },
  { href: '/fare-payment-policy', label: 'Fare & Payment Policy' },
  { href: '/customer-account-deletion', label: 'Customer Account Deletion' },
  ...DRIVER_LINKS,
];

const SOCIAL_LINKS = [
  { href: '/', label: 'Facebook', icon: 'fa-facebook' },
  { href: '/', label: 'Twitter', icon: 'fa-twitter' },
  { href: '/', label: 'Instagram', icon: 'fa-instagram' },
  { href: '/', label: 'WhatsApp', icon: 'fa-whatsapp' },
];

const useDesktopFooter = () => {
  const query = '(min-width: 992px)';
  const [desktop, setDesktop] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(query).matches
  ));

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return desktop;
};

const LinkList = ({ links, className }) => (
  <ul className={className}>
    {links.map((link) => (
      <li key={`${link.href}-${link.label}`}>
        <a href={link.href}>{link.label}</a>
      </li>
    ))}
  </ul>
);

const SocialLinks = () => (
  <div className="social-links">
    {SOCIAL_LINKS.map((item) => (
      <a key={item.label} href={item.href} className="social-icon" aria-label={item.label}>
        <i className={`fa ${item.icon}`} aria-hidden="true"></i>
      </a>
    ))}
  </div>
);

const FooterAccordion = ({ id, title, links }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const panelId = `${id}-panel`;
  const buttonId = `${id}-button`;

  useEffect(() => {
    if (panelRef.current) panelRef.current.inert = !open;
  }, [open]);

  return (
    <div className="footer-acc">
      <button
        type="button"
        id={buttonId}
        className="footer-acc-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{title}</span>
        <i className={`fa fa-chevron-down footer-acc-chevron${open ? ' is-open' : ''}`} aria-hidden="true"></i>
      </button>
      <div
        id={panelId}
        ref={panelRef}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!open}
        className={`footer-acc-panel${open ? ' is-open' : ''}`}
      >
        <div className="footer-acc-panel-inner">
          <ul className="footer-acc-links">
            {links.map((link) => (
              <li key={`${id}-${link.href}-${link.label}`}>
                <a className="footer-acc-link" href={link.href}>{link.label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const FooterCompact = ({ year }) => (
  <div className="footer-compact">
    <div className="footer-brand">
      <h2 className="footer-title">Easy Drop Taxi</h2>
      <p className="footer-brand-text">Reliable Intercity Taxi Services Across South India</p>
      <SocialLinks />
    </div>

    <div className="footer-cta">
      <h2 className="footer-cta-title">Need a Taxi?</h2>
      <p className="footer-cta-text">Book your next journey with EasyDropTaxi.</p>
      <div className="footer-cta-actions">
        <a className="footer-cta-primary" href="/#home-booking">Book a Taxi</a>
        <a className="footer-cta-secondary" href="/contact">Contact Us</a>
      </div>
    </div>

    <div className="footer-acc-group">
      <FooterAccordion id="footer-explore" title="Explore Services" links={EXPLORE_LINKS} />
      <FooterAccordion id="footer-routes" title="Popular Routes" links={ROUTE_LINKS} />
      <FooterAccordion id="footer-help" title="Help & Policies" links={HELP_LINKS} />
    </div>

    <div className="footer-contact">
      <h2 className="footer-contact-title">Contact Us</h2>
      <ul className="footer-contact-list">
        <li>
          <i className="fa fa-phone" aria-hidden="true"></i>
          <a href="tel:+919629954544">+91 9629954544</a>
        </li>
        <li>
          <i className="fa fa-envelope" aria-hidden="true"></i>
          <a href="mailto:support@easydroptaxi.com">support@easydroptaxi.com</a>
        </li>
        <li>
          <i className="fa fa-map-marker" aria-hidden="true"></i>
          <span>Coimbatore, Tamil Nadu, India</span>
        </li>
      </ul>
    </div>

    <div className="footer-bottom">
      <p>&copy; {year} Easy Drop Taxi. All rights reserved.</p>
      <p>
        <a href="/privacy-policy">Privacy Policy</a>
        <span aria-hidden="true"> · </span>
        <a href="/terms-and-conditions">Terms &amp; Conditions</a>
      </p>
      <p>
        Designed &amp; Developed by{' '}
        <a
          href="https://diya-solutions.vercel.app/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Diya Solutions
        </a>
      </p>
    </div>
  </div>
);

const FooterDesktop = ({ year }) => (
  <div className="footer-desktop container block-space">
    <div className="row">
      <div className="col-lg-3 col-md-6 mb-4">
        <h5 className="footer-title mb-3">Easy Drop Taxi</h5>
        <p className="footer-text">
          Your trusted partner for reliable and affordable taxi services in TamilNadu.
        </p>
        <SocialLinks />
      </div>

      <div className="col-lg-2 col-6 mb-4">
        <h5 className="footer-title mb-3">Quick Links</h5>
        <LinkList links={QUICK_LINKS} className="footer-links" />
      </div>

      <div className="col-lg-2 col-6 mb-4">
        <h5 className="footer-title mb-3">Services</h5>
        <LinkList links={SERVICE_LINKS} className="footer-links" />
      </div>

      <div className="col-lg-2 col-6 mb-4">
        <h5 className="footer-title mb-3">Driver App</h5>
        <LinkList links={DRIVER_LINKS} className="footer-links" />
      </div>

      <div className="col-lg-3 col-6 mb-4">
        <h5 className="footer-title mb-3">Contact Us</h5>
        <ul className="contact-info">
          <li>
            <i className="fa fa-phone"></i>
            <a href="tel:+919629954544">+91 9629954544</a>
          </li>
          <li>
            <i className="fa fa-envelope"></i>
            <a href="mailto:support@easydroptaxi.com">support@easydroptaxi.com</a>
          </li>
          <li>
            <i className="fa fa-map-marker"></i>
            <span>Coimbatore, Tamil Nadu, India</span>
          </li>
        </ul>
      </div>
    </div>

    <hr className="bg-secondary my-4" />

    <div className="row">
      <div className="col-md-6">
        <p className="footer-text mb-0">
          &copy; {year} Easy Drop Taxi. All rights reserved.
        </p>
      </div>
      <div className="col-md-6 text-md-end">
        <p className="footer-text mb-0">
          <a href="/privacy-policy" className="text-white-50">Privacy Policy</a> |{' '}
          <a href="/terms-and-conditions" className="text-white-50">Terms &amp; Conditions</a>
        </p>
      </div>
    </div>

    <hr className="bg-secondary my-3" />

    <div className="row">
      <div className="col-12 text-center">
        <p className="footer-text mb-0" style={{ fontSize: '0.85rem', color: '#888' }}>
          Designed &amp; Developed by{' '}
          <a
            href="https://diya-solutions.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary-color, #ffc61a)', fontWeight: 600, textDecoration: 'none' }}
          >
            Diya Solutions
          </a>
        </p>
      </div>
    </div>
  </div>
);

const Footer = () => {
  const year = new Date().getFullYear();
  const desktop = useDesktopFooter();

  return (
    <footer className="footer bg-dark text-white">
      <div className="footer-marquee-bar">
        <div className="footer-marquee-track">
          <span className="footer-marquee-content">
            🚖 We serve across: &nbsp;&nbsp; <strong>Tamil Nadu</strong> &nbsp;|&nbsp; <strong>Kerala</strong> &nbsp;|&nbsp; <strong>Pondicherry</strong> &nbsp;|&nbsp; <strong>Andhra Pradesh</strong> &nbsp;|&nbsp; <strong>Karnataka</strong> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            🚖 We serve across: &nbsp;&nbsp; <strong>Tamil Nadu</strong> &nbsp;|&nbsp; <strong>Kerala</strong> &nbsp;|&nbsp; <strong>Pondicherry</strong> &nbsp;|&nbsp; <strong>Andhra Pradesh</strong> &nbsp;|&nbsp; <strong>Karnataka</strong> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </span>
        </div>
      </div>
      {desktop ? <FooterDesktop year={year} /> : <FooterCompact year={year} />}
    </footer>
  );
};

export default Footer;
